import type { UnitRow } from '../data/joinCore';

/** Restrict a unit list to those belonging to a set of lot ids. `null`/empty → no scoping
 *  (return the input unchanged). A unit matches if its primary `lotId` is in the set, or any of
 *  its `lotIds` is (multi-lot / no-primary-lot units). Pure — no Parcel/lens dependency; the
 *  caller resolves which lots belong to the isolated category. */
export function scopeUnitsByLots(units: UnitRow[], lotIds: Set<string> | null): UnitRow[] {
  if (!lotIds || lotIds.size === 0) return units;
  return units.filter(u => (u.lotId != null && lotIds.has(u.lotId)) || u.lotIds.some(id => lotIds.has(id)));
}

export interface UnitGroup<T> { key: string; rows: T[]; }

/** Group rows by a key, preserving each row's incoming order WITHIN a group (so a pre-sorted input
 *  stays sorted inside each group). Blank keys fall into `blank` ('(none)'). Groups are ordered
 *  largest-first, the blank group always last, ties broken alphabetically. Pure. */
export function groupByKey<T>(rows: T[], keyOf: (r: T) => string, blank = '(none)'): UnitGroup<T>[] {
  const m = new Map<string, T[]>();
  for (const r of rows) {
    const k = keyOf(r) || blank;
    const arr = m.get(k); if (arr) arr.push(r); else m.set(k, [r]);
  }
  return [...m.entries()]
    .sort((a, b) =>
      (a[0] === blank ? 1 : 0) - (b[0] === blank ? 1 : 0) ||
      b[1].length - a[1].length ||
      a[0].localeCompare(b[0]))
    .map(([key, rows]) => ({ key, rows }));
}

/** Natural numeric compare of block-lot strings ("6-2" before "6-10"). Splits each string into
 *  numeric and non-numeric runs and compares run-by-run (numbers numerically, text lexically).
 *  Blank/empty block-lots sort last. */
export function compareBlockLot(a: string, b: string): number {
  const ea = (a || '').trim(), eb = (b || '').trim();
  if (!ea && !eb) return 0;
  if (!ea) return 1;            // blanks last
  if (!eb) return -1;
  const parts = (s: string) => s.match(/\d+|\D+/g) || [];
  const pa = parts(ea), pb = parts(eb);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i], y = pb[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    const nx = /^\d/.test(x), ny = /^\d/.test(y);
    if (nx && ny) { const d = parseInt(x, 10) - parseInt(y, 10); if (d) return d; }
    else { const d = x.localeCompare(y); if (d) return d; }
  }
  return 0;
}

/** Grid-header count label: "1,195 units" or, when isolated, "889 units · No permit". */
export function unitsGridCountLabel(count: number, isolatedLabel: string | null): string {
  const n = count.toLocaleString('en-US');
  const base = `${n} unit${count === 1 ? '' : 's'}`;
  return isolatedLabel ? `${base} · ${isolatedLabel}` : base;
}
